import { Cloud, CloudOff, Check, Loader2, AlertCircle } from 'lucide-react'
import { cn } from '@shared/lib/utils'
import type { AutoSaveStatus } from '../hooks/useAutoSave'

interface AutoSaveIndicatorProps {
  status: AutoSaveStatus
  lastSaved?: Date | null
  className?: string
}

function formatTimeSince(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function AutoSaveIndicator({ status, lastSaved, className }: AutoSaveIndicatorProps) {
  const renderContent = () => {
    switch (status) {
      case 'saving':
        return (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
            <span className="text-xs font-medium text-blue-600">Saving draft...</span>
          </>
        )

      case 'saved':
        return (
          <>
            <Check className="h-3.5 w-3.5 text-green-600" />
            <span className="text-xs font-medium text-green-600">
              Saved {lastSaved ? formatTimeSince(lastSaved) : ''}
            </span>
          </>
        )

      case 'error':
        return (
          <>
            <AlertCircle className="h-3.5 w-3.5 text-red-600" />
            <span className="text-xs font-medium text-red-600">Save failed</span>
          </>
        )

      case 'idle':
      default:
        if (lastSaved) {
          return (
            <>
              <Cloud className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Saved {formatTimeSince(lastSaved)}
              </span>
            </>
          )
        }
        return (
          <>
            <CloudOff className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Not saved</span>
          </>
        )
    }
  }

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {renderContent()}
    </div>
  )
}

interface DraftBadgeProps {
  className?: string
}

export function DraftBadge({ className }: DraftBadgeProps) {
  return (
    <div className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full',
      'bg-amber-50 border border-amber-200 text-amber-700',
      className
    )}>
      <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
      <span className="text-xs font-medium">Draft</span>
    </div>
  )
}
